import { useState, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { intlLocale } from "@/lib/locale";
import { computeAnalyticsHeaderSubtitle } from "@/lib/analyticsUtils";
import { computeFloorplanStats, filterStatementsToLenders } from "@/lib/floorplanStats";
import { useQuery } from "@tanstack/react-query";
import { AccessRestricted } from "@/components/access-restricted";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  ChevronDown, ChevronRight, RotateCcw, DollarSign,
  TrendingUp, TrendingDown, BarChart3, Info,
} from "lucide-react";
// ─── Helpers ───────────────────────────────────────────────────────────────────

function fmt$(v: number | null | undefined, decimals = 0): string {
  if (v == null || !Number.isFinite(v)) return "—";
  return v.toLocaleString(intlLocale(), { style: "currency", currency: "USD", maximumFractionDigits: decimals });
}
/** Round half-away-from-zero — matches toLocaleString(maximumFractionDigits:0) for all signs. */
function roundHalfAwayFromZero(v: number): number {
  return Math.sign(v) * Math.round(Math.abs(v));
}
function fmtPct(v: number): string {
  return `${(v * 100).toFixed(1)}%`;
}

// ─── Stat card ─────────────────────────────────────────────────────────────────

function StatCard({
  label, value, sub, icon: Icon, color, expandable, expanded, onToggle, expandLabel,
}: {
  label: string;
  value: string;
  sub?: string;
  icon: any;
  color?: string;
  expandable?: boolean;
  expanded?: boolean;
  onToggle?: () => void;
  expandLabel?: string;
}) {
  return (
    <Card
      className={expandable ? "cursor-pointer select-none hover:border-primary/40 hover:shadow-sm transition-all" : ""}
      onClick={expandable ? onToggle : undefined}
      data-testid={`stat-card-${label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <CardContent className="pt-4 pb-0 px-5">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <p className="text-xs text-muted-foreground leading-tight mb-1">{label}</p>
            <p className={`text-2xl font-bold tabular-nums truncate ${color || ""}`}>{value}</p>
            {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
          </div>
          <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
            <Icon className="w-4 h-4 text-primary" />
          </div>
        </div>
        {expandable && (
          <div className={`flex items-center justify-between mt-3 pt-2.5 pb-3 border-t ${expanded ? "border-primary/20" : "border-dashed border-border"}`}>
            <span className="text-xs font-medium text-primary/80">
              {expanded ? "Hide breakdown" : (expandLabel ?? "View breakdown")}
            </span>
            {expanded
              ? <ChevronDown className="w-3.5 h-3.5 text-primary/70" />
              : <ChevronRight className="w-3.5 h-3.5 text-primary/70" />
            }
          </div>
        )}
      </CardContent>
      {!expandable && <div className="pb-4" />}
    </Card>
  );
}

// ─── Main Page ─────────────────────────────────────────────────────────────────

export default function AnalyticsPage() {
  const { t } = useTranslation(["pages", "common"]);
  const { data: user } = useQuery<any>({ queryKey: ["/api/auth/me"], retry: false });
  const { data: latestPayload, isFetching: opAnalyticsFetching } = useQuery<any>({
    queryKey: ["/api/op-analytics/latest"],
    staleTime: 0,
  });
  const { data: statements = [] } = useQuery<any[]>({ queryKey: ["/api/parsed-statements"] });
  const { data: lenders = [] } = useQuery<any[]>({ queryKey: ["/api/lenders"] });

  // Only count statements linked to active lenders — ignores orphaned uploads
  const linkedStatements = useMemo(
    () => filterStatementsToLenders(statements, lenders),
    [statements, lenders]
  );

  // Auto avg turn days from per-unit receivable daysOutstanding (reacts to any statement change)
  const autoAvgTurnDays = useMemo(() => computeFloorplanStats(linkedStatements).avgTurnDays, [linkedStatements]);

  const inp = latestPayload?.input ?? null;
  const hasTaxReturn = !!inp;

  // Detect if a tax return extraction is currently in progress (persisted job in localStorage).
  // When pending, suppress the stale year label so dealers aren't confused by old data.
  const [isExtractionPending, setIsExtractionPending] = useState(false);
  useEffect(() => {
    if (!user?.id) return;
    const stored = localStorage.getItem(`taxReturnJobId:${user.id}`);
    setIsExtractionPending(!!stored);
  }, [user?.id]);

  const sourceTaxYear: number | null = (isExtractionPending || opAnalyticsFetching) ? null : (latestPayload?.sourceTaxYear ?? null);

  // ── Per-unit metrics from last computed tax return analytics ──
  const grossPerUnit: number | null = inp?.avgGrossProfitPerUnit ?? null;
  const annualUnitsSold: number = inp?.annualUnitsSold ?? 0;

  const totalExpenses: number | null = useMemo(() => {
    if (!inp) return null;
    if ((inp.annualTotalOperatingExpenses ?? 0) > 0) return inp.annualTotalOperatingExpenses;
    const dynamicItems: { label: string; amount: number }[] = inp.expenses?.dynamicItems ?? [];
    const sum = dynamicItems.reduce((s: number, item: { amount: number }) => s + (item.amount ?? 0), 0);
    return sum > 0 ? sum : null;
  }, [inp]);

  const expensesPerUnitRaw: number | null =
    totalExpenses != null && annualUnitsSold > 0 ? totalExpenses / annualUnitsSold : null;

  // Round each per-unit figure to the same integer that toLocaleString(maximumFractionDigits:0)
  // would display, then derive Net as their difference so all three cards are always consistent:
  // displayed Gross − displayed Expenses = displayed Net (no independent-rounding drift).
  const grossPerUnitRounded: number | null =
    grossPerUnit != null ? roundHalfAwayFromZero(grossPerUnit) : null;
  const expensesPerUnit: number | null =
    expensesPerUnitRaw != null ? roundHalfAwayFromZero(expensesPerUnitRaw) : null;
  const netProfitPerUnit: number | null =
    grossPerUnitRounded != null && expensesPerUnit != null
      ? grossPerUnitRounded - expensesPerUnit
      : null;

  // ── Inventory turns from receivables ──
  const inventoryTurns: number | null = autoAvgTurnDays ? 365 / autoAvgTurnDays : null;

  const headerSubtitle = computeAnalyticsHeaderSubtitle(hasTaxReturn, latestPayload?.lastSavedAt, t, intlLocale());

  if (user?.memberPermissions && !user.memberPermissions.analyticsAccess) {
    return <AccessRestricted feature={t("pages:analytics.accessFeature")} />;
  }

  return (
    <div className="p-4 md:p-6 space-y-6 max-w-screen-xl">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{t("pages:analytics.title")}</h1>
          <p className="text-sm text-muted-foreground mt-0.5" data-testid="text-analytics-header-subtitle">
            {headerSubtitle}
          </p>
        </div>
      </div>

      {/* ── Empty state ── */}
      {!hasTaxReturn && (
        <Card>
          <CardContent className="py-16 text-center">
            <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
              <BarChart3 className="w-7 h-7 text-primary opacity-60" />
            </div>
            <h3 className="font-semibold text-base mb-1">{t("pages:analytics.emptyTitle")}</h3>
            <p className="text-sm text-muted-foreground max-w-sm mx-auto">
              {t("pages:analytics.emptyBefore")} <strong>{t("pages:analytics.emptyDealershipAnalysis")}</strong>{t("pages:analytics.emptyAfter")}
            </p>
          </CardContent>
        </Card>
      )}

      {/* ── TOP: Per-unit stat cards ── */}
      {hasTaxReturn && (
        <>
          <div>
            <p className="text-xs text-muted-foreground uppercase tracking-wide font-semibold mb-3">{sourceTaxYear ? t("pages:analytics.perUnitFromYear", { year: sourceTaxYear }) : t("pages:analytics.perUnitMostRecent")}</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <StatCard
                label={t("pages:analytics.grossProfitUnit")}
                value={fmt$(grossPerUnitRounded)}
                sub={annualUnitsSold > 0 ? t("pages:analytics.acrossUnitsSold", { count: annualUnitsSold.toLocaleString(intlLocale()) }) : undefined}
                icon={DollarSign}
                color={grossPerUnitRounded != null && grossPerUnitRounded >= 0 ? "text-chart-3" : "text-destructive"}
              />
              <StatCard
                label={t("pages:analytics.totalExpensesUnit")}
                value={fmt$(expensesPerUnit)}
                icon={BarChart3}
              />
              <StatCard
                label={t("pages:analytics.netProfitUnit")}
                value={fmt$(netProfitPerUnit)}
                sub={
                  netProfitPerUnit != null && grossPerUnitRounded != null && grossPerUnitRounded > 0
                    ? t("pages:analytics.ofGross", { pct: fmtPct(netProfitPerUnit / grossPerUnitRounded) })
                    : undefined
                }
                icon={netProfitPerUnit != null && netProfitPerUnit >= 0 ? TrendingUp : TrendingDown}
                color={netProfitPerUnit != null && netProfitPerUnit >= 0 ? "text-chart-3" : "text-destructive"}
              />
            </div>
          </div>


          {/* Expense breakdown — now lives in Dealership Analysis > Expenses tab */}
          <Card className="border-dashed">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <BarChart3 className="w-4 h-4 text-muted-foreground" />
                {t("pages:analytics.expenseBreakdownTitle")}
              </CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col items-center justify-center gap-3 py-6 text-center">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                <BarChart3 className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="font-semibold text-sm">{t("pages:analytics.expenseMovedTitle")}</p>
                <p className="text-xs text-muted-foreground mt-1 max-w-xs mx-auto">
                  {t("pages:analytics.expenseMovedPrefix")} <strong>{t("pages:analytics.expenseMovedTab")}</strong> {t("pages:analytics.expenseMovedSuffix")}
                </p>
              </div>
            </CardContent>
          </Card>

        </>
      )}

      {/* ── BOTTOM: Inventory turns ── */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2">
            <RotateCcw className="w-4 h-4 text-primary" />{t("pages:analytics.inventoryTurnsTitle")}
            <Info className="w-4 h-4 text-muted-foreground ml-auto" aria-label={t("pages:analytics.inventoryTurnsAria")} />
          </CardTitle>
          <CardDescription>
            {autoAvgTurnDays != null
              ? t("pages:analytics.inventoryTurnsDescYes", { days: autoAvgTurnDays })
              : t("pages:analytics.inventoryTurnsDescNo")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {inventoryTurns != null ? (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="p-4 rounded-xl bg-primary/8 border border-primary/20 text-center">
                  <p className="text-3xl font-black text-primary tabular-nums" data-testid="text-inventory-turns">
                    {inventoryTurns.toFixed(1)}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">{t("pages:analytics.turnsPerYear")}</p>
                </div>
                <div className="p-4 rounded-xl bg-muted/40 text-center">
                  <p className="text-3xl font-black tabular-nums" data-testid="text-avg-turn-days">
                    {autoAvgTurnDays}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">{t("pages:analytics.avgDaysPerUnit")}</p>
                </div>
              </div>
              <div className={`flex items-center gap-2 p-3 rounded-lg text-xs ${
                inventoryTurns >= 7
                  ? "bg-chart-3/10 border border-chart-3/20 text-chart-3"
                  : inventoryTurns >= 5
                    ? "bg-yellow-500/10 border border-yellow-500/20 text-yellow-700 dark:text-yellow-400"
                    : "bg-destructive/10 border border-destructive/20 text-destructive"
              }`}>
                <Info className="w-3.5 h-3.5 shrink-0" />
                {inventoryTurns >= 7
                  ? t("pages:analytics.healthy", { turns: inventoryTurns.toFixed(1) })
                  : inventoryTurns >= 5
                    ? t("pages:analytics.slightlyBelow", { turns: inventoryTurns.toFixed(1) })
                    : t("pages:analytics.belowTarget", { turns: inventoryTurns.toFixed(1) })}
              </div>
            </div>
          ) : (
            <div className="py-8 text-center">
              <RotateCcw className="w-8 h-8 text-muted-foreground/30 mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">{t("pages:analytics.noReceivableData")}</p>
              <p className="text-xs text-muted-foreground/60 mt-0.5">{t("pages:analytics.uploadFloorplanHint")}</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
